/**
 * Pure 3"-grid cabinet-module solver for manually-drawn cabinet runs.
 *
 * Packs a free wall span into the fewest, widest standard base-cabinet
 * modules plus at most one trailing filler strip. No I/O; no codebase
 * dependencies beyond the inch->meter constant. See
 * claude-docs/2026-06-13-manual-cabinet-runs-design.md §4.
 */

const IN = 0.0254;

/** Standard 3"-grid base-cabinet widths (inches). */
const BASE_MODULE_WIDTHS_IN = [9, 12, 15, 18, 21, 24, 27, 30, 33, 36];
const MAX_MODULE_IN = 36;

/**
 * The widest module a fill may build: a family that names its widths sells
 * up to its own widest (Venus: 42" bases), every other family the 3"-grid's
 * 36" — the convention the first cabinet-run design took from typical stock
 * lines (2026-06-13), not a constraint of anything built.
 */
function widestModuleIn(opts = {}) {
  const allowed = opts.allowedWidthsIn;
  return Array.isArray(allowed) && allowed.length
    ? Math.max(...allowed)
    : MAX_MODULE_IN;
}
const MIN_MODULE_IN = 9;
/**
 * The narrowest filler a run emits (inches). A leftover under it is left to the
 * installer to scribe — a strip that thin is not a filler anyone cuts or
 * buys — and a gap to a wall under it is no gap at all: the run ends AT the
 * wall (`fillZoneBounds`, `SEAT_FLUSH`, `runEndKind` borrow it for exactly
 * that). ¼" until 2026-10-20, which billed a 0.26" "filler" as a part.
 */
const MIN_FILLER_IN = 0.5;

function asWidth(widthIn) {
  return { widthIn, widthMeters: widthIn * IN };
}

/**
 * Decompose a width that is an exact multiple of 3" and >= MIN_MODULE_IN
 * into standard grid modules, each in [9, 36]". Fewest, widest cabinets
 * (fill with 36s); when the remainder would be a sub-9" sliver (3" or 6")
 * borrow the last 36 and split (36 + remainder) into two grid modules so
 * no illegal narrow cabinet is emitted.
 *
 * @param {number} totalIn - MUST be an exact multiple of 3 and >= MIN_MODULE_IN
 *   (callers pass Math.floor(span/3)*3). Not validated; do not call directly
 *   with arbitrary input.
 */
function decomposeMultipleOf3(totalIn) {
  const modules = [];
  const q = Math.floor(totalIn / MAX_MODULE_IN);
  const r = totalIn - q * MAX_MODULE_IN;
  if (r === 0) {
    for (let i = 0; i < q; i += 1) modules.push(MAX_MODULE_IN);
  } else if (r >= MIN_MODULE_IN) {
    for (let i = 0; i < q; i += 1) modules.push(MAX_MODULE_IN);
    modules.push(r);
  } else {
    // r is 3 or 6 — borrow the last 36 and split (36 + r) into two modules.
    for (let i = 0; i < q - 1; i += 1) modules.push(MAX_MODULE_IN);
    const combined = MAX_MODULE_IN + r; // 39 or 42
    const a = Math.round(combined / 2 / 3) * 3; // nearest multiple of 3
    const b = combined - a;
    modules.push(Math.max(a, b), Math.min(a, b));
  }
  return modules;
}

/**
 * @param {number} spanMeters - free span to fill, in meters.
 * @returns {{ modules: Array<{widthIn:number, widthMeters:number}>,
 *            filler: {widthIn:number, widthMeters:number} | null }}
 */
/** Quarter-of-an-inch is the packer's grid; a custom shop works finer. */
const CONTINUOUS_STEP_IN = 1 / 16;

/**
 * Divide a span into EQUAL boxes of any width — a `continuous` family, where
 * every width is buildable, so nothing is left over and there is no filler.
 *
 * The count is the fewest boxes that keep every one at or under
 * `MAX_MODULE_IN`, but never so many that they fall under `MIN_MODULE_IN`
 * (even a custom shop has a narrowest box worth building; below it the whole
 * span is a filler strip, exactly as the grid path leaves it). Widths are cut
 * to a 16th of an inch — a buildable number — and the rounding remainder goes
 * to ONE box, so the boxes still sum to the span exactly.
 */
function divideContinuous(spanIn) {
  if (spanIn < MIN_MODULE_IN) {
    return {
      modules: [],
      filler: spanIn >= MIN_FILLER_IN ? asWidth(spanIn) : null,
    };
  }
  const n = Math.max(
    1,
    Math.min(
      Math.ceil(spanIn / MAX_MODULE_IN),
      Math.floor(spanIn / MIN_MODULE_IN + 1e-9)
    )
  );
  const step = CONTINUOUS_STEP_IN;
  // `n` is chosen so the even share never exceeds MAX_MODULE_IN, and the base
  // floors that share to a buildable 16th.
  const base = Math.floor(spanIn / n / step + 1e-9) * step;
  const modulesIn = new Array(n).fill(base);
  // Hand the flooring remainder (< n × 1/16") back one step at a time rather
  // than dumping it on one box: a single box would take the whole of it and
  // could finish OVER 36" — 143.7" split four ways is 35.875 × 3 + 36.075,
  // and that last box is not a cabinet any more. Capping each hand-out at
  // MAX_MODULE_IN also makes the cap structural rather than incidental.
  let remainder = spanIn - base * n;
  for (let i = 0; i < n && remainder > 1e-9; i += 1) {
    const give = Math.min(step, remainder, MAX_MODULE_IN - modulesIn[i]);
    modulesIn[i] = Math.round((modulesIn[i] + give) * 1e6) / 1e6;
    remainder -= give;
  }
  modulesIn.sort((a, b) => b - a);
  return { modules: modulesIn.map(asWidth), filler: null };
}

/**
 * Fill `spanIn` from an explicit width set (a product family's stock
 * widths, phase 2): unbounded knapsack on a quarter-inch grid, maximising
 * covered length; the remainder becomes the filler. Largest modules first,
 * matching the default solver's ordering.
 */
function packAllowedWidths(spanIn, allowedWidthsIn) {
  const Q = 4; // quarter-inch resolution
  const cap = Math.max(0, Math.floor(spanIn * Q + 1e-6));
  const ws = [...new Set(allowedWidthsIn)]
    .map((w) => Math.round(w * Q))
    .filter((w) => w > 0 && w <= cap);
  // Minimise module COUNT among exact covers, so 60" packs 36+24, not
  // five 12s — a kitchen wants the fewest, largest boxes a family offers.
  const INF = 0x3fffffff;
  const cnt = new Int32Array(cap + 1).fill(INF);
  const choice = new Int32Array(cap + 1).fill(-1);
  cnt[0] = 0;
  for (let v = 1; v <= cap; v++) {
    for (let i = 0; i < ws.length; i++) {
      if (ws[i] <= v && cnt[v - ws[i]] + 1 < cnt[v]) {
        cnt[v] = cnt[v - ws[i]] + 1;
        choice[v] = i;
      }
    }
  }
  let v = cap;
  while (v > 0 && cnt[v] === INF) v--;
  const modulesIn = [];
  for (let cur = v; cur > 0; cur -= ws[choice[cur]]) {
    modulesIn.push(ws[choice[cur]] / Q);
  }
  modulesIn.sort((a, b) => b - a);
  return { coveredIn: v / Q, modulesIn };
}

function solveModuleSequence(spanMeters, opts = {}) {
  if (!Number.isFinite(spanMeters)) return { modules: [], filler: null };

  // Snap away sub-micron FP noise so exact-inch spans floor correctly.
  const spanIn = Math.round((spanMeters / IN) * 1e6) / 1e6;

  // The run's product family decides how widths are chosen. `continuous` and
  // an explicit width set are the two families that override the default
  // 3-inch-multiple decomposition below; a grid family passes neither.
  if (opts.continuous) return divideContinuous(spanIn);

  const allowed = opts.allowedWidthsIn;
  if (Array.isArray(allowed) && allowed.length) {
    const { coveredIn, modulesIn } = packAllowedWidths(spanIn, allowed);
    const fillerIn = Math.round((spanIn - coveredIn) * 1e6) / 1e6;
    return {
      modules: modulesIn.map(asWidth),
      filler: fillerIn >= MIN_FILLER_IN ? asWidth(fillerIn) : null,
    };
  }

  if (spanIn < MIN_MODULE_IN) {
    return {
      modules: [],
      filler: spanIn >= MIN_FILLER_IN ? asWidth(spanIn) : null,
    };
  }

  const tFloorIn = Math.floor(spanIn / 3) * 3; // largest 3"-multiple <= span
  const fillerIn = Math.round((spanIn - tFloorIn) * 1e6) / 1e6; // [0, 3)

  return {
    modules: decomposeMultipleOf3(tFloorIn).map(asWidth),
    filler: fillerIn >= MIN_FILLER_IN ? asWidth(fillerIn) : null,
  };
}

/**
 * The fewest modules the grid's widest width allows (`MAX_MODULE_IN` at most),
 * as EQUAL as the grid allows, largest first — a tall span's split (user ruling 2026-09-06: a 48"
 * pantry wall is 24 + 24, not the base run's greedy 36 + 12). The grid is the
 * family's width list when it has one, else the 3" base grid. Every module
 * starts at the largest grid width not above the even share, the remainder is
 * handed back to the modules one grid step at a time, and what the grid
 * cannot absorb is the filler — exactly as `solveModuleSequence` leaves it.
 * Same return shape as `solveModuleSequence`.
 */
function solveEqualModuleSequence(spanMeters, opts = {}) {
  if (!Number.isFinite(spanMeters)) return { modules: [], filler: null };
  const spanIn = Math.round((spanMeters / IN) * 1e6) / 1e6;
  // A continuous family divides evenly by construction — the split this
  // function forces onto a grid is what `divideContinuous` already returns.
  if (opts.continuous) return divideContinuous(spanIn);
  const allowed = opts.allowedWidthsIn;
  const grid = [
    ...new Set(
      Array.isArray(allowed) && allowed.length ? allowed : BASE_MODULE_WIDTHS_IN
    ),
  ]
    .filter((w) => w > 0 && w <= widestModuleIn(opts))
    .sort((a, b) => a - b);
  const widest = grid.length ? grid[grid.length - 1] : MAX_MODULE_IN;
  const narrowest = grid.length ? grid[0] : MIN_MODULE_IN;
  // The fewest that COVER the span at the grid's widest — but never more than
  // actually FIT at its narrowest, or the even share falls under the grid's
  // own minimum and the whole span drops out as filler (a 24"-only family at
  // 100" covers with five and fits four).
  const n = Math.max(
    1,
    Math.min(Math.ceil(spanIn / widest), Math.floor(spanIn / narrowest + 1e-9))
  );
  const share = spanIn / n;
  const base = grid.filter((w) => w <= share + 1e-9).pop();
  if (base == null) {
    // Narrower than the narrowest module: filler only.
    return {
      modules: [],
      filler: spanIn >= MIN_FILLER_IN ? asWidth(spanIn) : null,
    };
  }
  const modulesIn = new Array(n).fill(base);
  let remainder = spanIn - n * base;
  let raised = true;
  while (raised) {
    raised = false;
    for (let i = 0; i < n; i += 1) {
      const next = grid.find((w) => w > modulesIn[i]);
      if (next == null) continue;
      const step = next - modulesIn[i];
      if (step <= remainder + 1e-9) {
        modulesIn[i] = next;
        remainder -= step;
        raised = true;
      }
    }
  }
  modulesIn.sort((a, b) => b - a);
  const fillerIn = Math.round(remainder * 1e6) / 1e6;
  return {
    modules: modulesIn.map(asWidth),
    filler: fillerIn >= MIN_FILLER_IN ? asWidth(fillerIn) : null,
  };
}

/**
 * Arrange a set of module widths so the sequence reads the same from either
 * end: equal pairs go to the outside, the odd one out to the middle —
 * 30 · 27 · 27 becomes 27 · 30 · 27. `solveEqualModuleSequence` returns its
 * modules largest-first, which is an ORDER OF DISCOVERY, not a design: a tall
 * span hides it (its boxes are one unit), a row of uppers at eye level does
 * not.
 *
 * With two odd ones out (33 · 30) no order is symmetric, and the widths come
 * back largest-first as the solver had them — the caller, which knows what
 * stands at each end, may turn them round.
 *
 * Unit-agnostic: inches or metres, it only compares.
 */
function symmetricOrder(widths) {
  const sorted = [...widths].sort((a, b) => b - a);
  const groups = [];
  for (const w of sorted) {
    const last = groups[groups.length - 1];
    if (last && Math.abs(last.w - w) < 1e-9) last.n += 1;
    else groups.push({ w, n: 1 });
  }
  const odd = groups.filter((g) => g.n % 2 === 1);
  if (odd.length > 1) return sorted;
  const half = groups.flatMap((g) => new Array(Math.floor(g.n / 2)).fill(g.w));
  return [...half, ...odd.map((g) => g.w), ...[...half].reverse()];
}

/**
 * The WIDEST single cabinet the family sells that fits `spanMeters`, in metres
 * — 0 when it sells none that narrow.
 *
 * Not `solveModuleSequence(span).modules[0]`: that solver COVERS a span, so 27"
 * under a stock family is 15" + 12" and its widest module is 15", where the
 * widest cabinet that fits is 24". A box that has to stay ONE box — an upper
 * over a single lower bay — asks this instead.
 */
function widestModuleWithin(spanMeters, opts = {}) {
  if (!Number.isFinite(spanMeters)) return 0;
  const spanIn = Math.round((spanMeters / IN) * 1e6) / 1e6;
  const capIn = Math.min(spanIn, MAX_MODULE_IN);
  if (capIn < MIN_MODULE_IN) return 0;
  let widthIn;
  if (opts.continuous) {
    // Any width, cut to a buildable 16th like `divideContinuous`.
    widthIn =
      Math.floor(capIn / CONTINUOUS_STEP_IN + 1e-9) * CONTINUOUS_STEP_IN;
  } else if (
    Array.isArray(opts.allowedWidthsIn) &&
    opts.allowedWidthsIn.length
  ) {
    widthIn = Math.max(
      0,
      ...opts.allowedWidthsIn.filter((w) => w > 0 && w <= capIn + 1e-9)
    );
  } else {
    widthIn = Math.floor(capIn / 3 + 1e-9) * 3;
  }
  return widthIn >= MIN_MODULE_IN ? asWidth(widthIn).widthMeters : 0;
}

module.exports = {
  solveModuleSequence,
  widestModuleIn,
  widestModuleWithin,
  solveEqualModuleSequence,
  symmetricOrder,
  BASE_MODULE_WIDTHS_IN,
  MAX_MODULE_IN,
  MIN_MODULE_IN,
  MIN_FILLER_IN,
  CONTINUOUS_STEP_IN,
};
